<script setup lang="ts">
import { computed } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useEntityStore } from '@/stores/entityStore';
import { describeFilterRule } from '@/components/DataTable/describeFilterRule';
import { filterLeafRules, LIST_SEARCH_RULE_ID, withoutRule } from '@/utils/listSearch';

/**
 * The filters in use, one chip each, under a list's toolbar. × removes that rule.
 * The search box's rule is not a chip, because the search box already shows it.
 * The row is hidden when there are no chips. The toolbar's funnel button is the
 * one way to open the filter builder, including to add the first rule.
 */

const entityStore = useEntityStore();
const { filter, setFilter } = usePersistentTable();

const chips = computed(() =>
    filterLeafRules(filter.value)
        .filter((rule) => rule.id !== LIST_SEARCH_RULE_ID)
        .map((rule) => ({
            id: rule.id,
            ...describeFilterRule(entityStore.selectedEntityId, rule),
        })),
);

function remove(ruleId: string) {
    if (filter.value) {
        setFilter(withoutRule(filter.value, ruleId));
    }
}
</script>

<template>
  <div v-if="chips.length > 0" class="flex flex-wrap items-center gap-1.5 px-4 pb-1" data-testid="filter-chips">
    <span
      v-for="chip in chips"
      :key="chip.id"
      data-testid="filter-chip"
      class="inline-flex h-7 items-center gap-1.5 rounded-full bg-background pl-3 pr-1 text-xs text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]"
    >
      <span>{{ chip.column }} {{ chip.criterion }}</span>
      <b v-if="chip.value" class="max-w-48 truncate font-semibold text-foreground">{{ chip.value }}</b>
      <button
        type="button"
        :aria-label="`Remove filter ${chip.column} ${chip.criterion} ${chip.value}`"
        class="rounded-full p-1 hover:bg-muted hover:text-foreground"
        @click="remove(chip.id)"
      >
        <XMarkIcon class="size-3" aria-hidden="true" />
      </button>
    </span>
  </div>
</template>
